import { useState, useEffect, useCallback } from "react";
import api from "../../services/api";

const STATUS_CONFIG = {
  trial:     { label: "Essai",        icon: "🟡", color: "#d97706", bg: "#d9770615" },
  actif:     { label: "Actif",        icon: "🟢", color: "#1a9e72", bg: "#1a9e7215" },
  suspendu:  { label: "Suspendu",     icon: "🔵", color: "#185fa5", bg: "#185fa515" },
  résilié:   { label: "Résilié",      icon: "⚫", color: "#6b7280", bg: "#6b728015" },
  impayé:    { label: "Impayé",       icon: "🔴", color: "#e24b4a", bg: "#e24b4a15" },
};

function fmt(n) { return (n||0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(d) { if (!d) return "—"; return new Date(d + "T00:00:00").toLocaleDateString("fr-FR"); }
function daysLeft(d) { if (!d) return null; const n = Math.ceil((new Date(d + "T00:00:00") - new Date()) / 86400000); return n; }

// ── Plan Card (Preview) ───────────────────────────────────────────────────
function PlanCard({ plan, onEdit, onDelete }) {
  const features  = Array.isArray(plan.features) ? plan.features : [];
  const discount  = plan.price_monthly > 0 && plan.price_yearly > 0
    ? Math.round((1 - (plan.price_yearly / 12) / plan.price_monthly) * 100)
    : 0;

  return (
    <div style={{ border: plan.is_active ? "2px solid var(--g)" : "1px solid #e5e7eb", borderRadius: 12, padding: "18px 20px", background: plan.is_active ? "#f0fdf4" : "#fafafa", position: "relative" }}>
      {!plan.is_active && <div style={{ position: "absolute", top: 10, right: 12, fontSize: 10, background: "#6b7280", color: "#fff", padding: "2px 7px", borderRadius: 8 }}>Inactif</div>}
      <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 4 }}>{plan.name}</div>
      {plan.description && <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>{plan.description}</div>}
      <div style={{ display: "flex", gap: 16, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "var(--g)" }}>{plan.price_monthly || 0} €</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>/ mois</div>
        </div>
        {plan.price_yearly > 0 && (
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{plan.price_yearly} €</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>/ an {discount > 0 && <span style={{ color: "#1a9e72", fontWeight: 700 }}>(-{discount}%)</span>}</div>
          </div>
        )}
      </div>
      {plan.trial_days > 0 && <div style={{ fontSize: 11, background: "#7c3aed15", color: "#7c3aed", padding: "3px 8px", borderRadius: 8, display: "inline-block", marginBottom: 10, fontWeight: 700 }}>✨ {plan.trial_days} jours d'essai gratuit</div>}
      {features.length > 0 && (
        <ul style={{ margin: "0 0 12px", padding: 0, listStyle: "none" }}>
          {features.slice(0, 5).map((f, i) => <li key={i} style={{ fontSize: 12, padding: "3px 0", display: "flex", gap: 6 }}><span style={{ color: "var(--g)" }}>✓</span>{f}</li>)}
          {features.length > 5 && <li style={{ fontSize: 11, color: "var(--text-muted)" }}>+{features.length - 5} autres…</li>}
        </ul>
      )}
      {(plan.max_users || plan.max_products) && (
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>
          {plan.max_users && <span>👤 {plan.max_users} utilisateurs max · </span>}
          {plan.max_products && <span>📦 {plan.max_products} produits max</span>}
        </div>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <button className="btn" style={{ fontSize: 11 }} onClick={() => onEdit(plan)}>✏️ Modifier</button>
        <button className="btn" style={{ fontSize: 11, color: "#e24b4a" }} onClick={() => onDelete(plan.id)}>🗑</button>
      </div>
    </div>
  );
}

// ── Plan Form Modal ───────────────────────────────────────────────────────
function PlanFormModal({ initial, onClose, onSaved, currentCompanyId }) {
  const [form, setForm] = useState(initial || {
    name: "", slug: "", description: "", features: [], price_monthly: "", price_yearly: "",
    trial_days: "0", is_active: true, max_users: "", max_products: "", custom_fields: {},
  });
  const [newFeature, setNewFeature] = useState("");
  const [newCfKey, setNewCfKey]     = useState("");
  const [newCfVal, setNewCfVal]     = useState("");
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState("");
  const isEdit = !!initial?.id;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addFeature = () => { if (!newFeature.trim()) return; set("features", [...(form.features||[]), newFeature.trim()]); setNewFeature(""); };
  const removeFeature = (i) => set("features", form.features.filter((_, idx) => idx !== i));
  const addCF = () => { if (!newCfKey.trim()) return; set("custom_fields", { ...(form.custom_fields||{}), [newCfKey]: newCfVal }); setNewCfKey(""); setNewCfVal(""); };
  const removeCF = (k) => { const f = { ...form.custom_fields }; delete f[k]; set("custom_fields", f); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      const data = { ...form, company_id: currentCompanyId, price_monthly: parseFloat(form.price_monthly)||0, price_yearly: parseFloat(form.price_yearly)||0, trial_days: parseInt(form.trial_days)||0 };
      if (isEdit) await api.abonnements.updateClientPlan(initial.id, data);
      else        await api.abonnements.createClientPlan(data);
      onSaved(); onClose();
    } catch (err) { setError(err.message); setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
      <div className="card" style={{ width: "100%", maxWidth: 600 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{isEdit ? "✏️ Modifier le plan" : "✨ Nouveau plan tarifaire"}</span>
          <button className="btn" style={{ marginLeft: "auto" }} onClick={onClose}>✕</button>
        </div>
        {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Nom du plan *</label>
              <input className="input" required value={form.name} onChange={e => set("name", e.target.value)} placeholder="Starter, Pro, Business..." />
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Description</label>
              <input className="input" value={form.description||""} onChange={e => set("description", e.target.value)} placeholder="Idéal pour les petites équipes..." />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Prix mensuel (€)</label>
              <input className="input" type="number" step="0.01" value={form.price_monthly} onChange={e => set("price_monthly", e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Prix annuel (€) <span style={{ fontSize: 10, color: "var(--text-muted)" }}>total, pas /mois</span></label>
              <input className="input" type="number" step="0.01" value={form.price_yearly} onChange={e => set("price_yearly", e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Jours d'essai gratuit</label>
              <input className="input" type="number" min="0" value={form.trial_days} onChange={e => set("trial_days", e.target.value)} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 18 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                <input type="checkbox" checked={form.is_active} onChange={e => set("is_active", e.target.checked)} /> Plan actif
              </label>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Max utilisateurs</label>
              <input className="input" type="number" value={form.max_users||""} onChange={e => set("max_users", e.target.value || null)} placeholder="Illimité" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Max produits</label>
              <input className="input" type="number" value={form.max_products||""} onChange={e => set("max_products", e.target.value || null)} placeholder="Illimité" />
            </div>
          </div>

          {/* Features */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6 }}>Fonctionnalités incluses</label>
            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
              <input className="input" value={newFeature} onChange={e => setNewFeature(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addFeature())} placeholder="Ex: Import Excel illimité" style={{ flex: 1 }} />
              <button type="button" className="btn" onClick={addFeature}>+ Ajouter</button>
            </div>
            {(form.features||[]).map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", background: "#f9fafb", borderRadius: 6, marginBottom: 4 }}>
                <span style={{ color: "var(--g)", fontWeight: 700 }}>✓</span>
                <span style={{ flex: 1, fontSize: 12 }}>{f}</span>
                <button type="button" style={{ background: "none", border: "none", color: "#e24b4a", cursor: "pointer", fontSize: 12 }} onClick={() => removeFeature(i)}>✕</button>
              </div>
            ))}
          </div>

          {/* Custom fields */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6 }}>Champs personnalisés (clé → valeur)</label>
            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
              <input className="input" value={newCfKey} onChange={e => setNewCfKey(e.target.value)} placeholder="Clé" style={{ flex: 1 }} />
              <input className="input" value={newCfVal} onChange={e => setNewCfVal(e.target.value)} placeholder="Valeur" style={{ flex: 2 }} />
              <button type="button" className="btn" onClick={addCF}>+ Ajouter</button>
            </div>
            {Object.entries(form.custom_fields||{}).map(([k, v]) => (
              <div key={k} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 8px", background: "#f9fafb", borderRadius: 6, marginBottom: 4, fontSize: 12 }}>
                <strong>{k}</strong><span>→</span><span style={{ flex: 1 }}>{v}</span>
                <button type="button" style={{ background: "none", border: "none", color: "#e24b4a", cursor: "pointer" }} onClick={() => removeCF(k)}>✕</button>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? "Enregistrement…" : isEdit ? "Sauvegarder" : "Créer le plan"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Create Subscription Modal ─────────────────────────────────────────────
function SubFormModal({ initial, plans, onClose, onSaved, currentCompanyId }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(initial ? {
    client_name: initial.client_name || "", client_email: initial.client_email || "",
    plan_id: initial.plan_id || "", billing_cycle: initial.billing_cycle || "mensuel",
    start_date: initial.start_date || new Date().toISOString().slice(0, 10),
    amount: String(initial.amount ?? ""), payment_method: initial.payment_method || "virement",
    auto_renew: initial.auto_renew !== 0, status: initial.status || "trial",
  } : {
    client_name: "", client_email: "", plan_id: "", billing_cycle: "mensuel",
    start_date: new Date().toISOString().slice(0, 10), amount: "", payment_method: "virement",
    auto_renew: true, status: "trial",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectPlan = (planId) => {
    const plan = plans.find(p => p.id === parseInt(planId));
    if (plan) set("amount", String(form.billing_cycle === "annuel" ? plan.price_yearly : plan.price_monthly));
    set("plan_id", planId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      const plan = plans.find(p => p.id === parseInt(form.plan_id));
      const payload = {
        ...form, company_id: currentCompanyId,
        plan_name: plan?.name || "",
        amount: parseFloat(form.amount),
      };
      if (isEdit) await api.abonnements.updateClientSub(initial.id, payload);
      else        await api.abonnements.createClientSub(payload);
      onSaved(); onClose();
    } catch (err) { setError(err.message); setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
      <div className="card" style={{ width: "100%", maxWidth: 560 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{isEdit ? "✏️ Modifier l'abonnement" : "+ Nouvel abonnement client"}</span>
          <button className="btn" style={{ marginLeft: "auto" }} onClick={onClose}>✕</button>
        </div>
        {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Nom client *</label>
              <input className="input" required value={form.client_name} onChange={e => set("client_name", e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Email client</label>
              <input className="input" type="email" value={form.client_email} onChange={e => set("client_email", e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Plan</label>
              <select className="input" value={form.plan_id} onChange={e => selectPlan(e.target.value)}>
                <option value="">— Sélectionner un plan —</option>
                {plans.map(p => <option key={p.id} value={p.id}>{p.name} ({p.price_monthly}€/mois)</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Cycle</label>
              <select className="input" value={form.billing_cycle} onChange={e => set("billing_cycle", e.target.value)}>
                <option value="mensuel">Mensuel</option>
                <option value="annuel">Annuel</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Montant (€) *</label>
              <input className="input" type="number" step="0.01" required value={form.amount} onChange={e => set("amount", e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Méthode paiement</label>
              <select className="input" value={form.payment_method} onChange={e => set("payment_method", e.target.value)}>
                {["stripe","gocardless","paypal","virement","prélèvement","chèque","autre"].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Date de début *</label>
              <input className="input" type="date" required value={form.start_date} onChange={e => set("start_date", e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Statut initial</label>
              <select className="input" value={form.status} onChange={e => set("status", e.target.value)}>
                <option value="trial">Essai gratuit</option>
                <option value="actif">Actif</option>
              </select>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? "Enregistrement…" : isEdit ? "Sauvegarder" : "Créer l'abonnement"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Main ClientSubsTab — plans + abonnés en deux sous-onglets
// ════════════════════════════════════════════════════════════════════════
export default function ClientSubsTab({ currentCompanyId, onNavigate }) {
  const [innerTab, setInnerTab] = useState("plans");
  const [plans, setPlans]       = useState([]);
  const [subs, setSubs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [planFilter, setPlanFilter]     = useState("");
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [editPlan, setEditPlan]         = useState(null);
  const [showSubForm, setShowSubForm]   = useState(false);
  const [editSub, setEditSub]           = useState(null);
  const [toast, setToast]       = useState("");
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [paymentLinkModal, setPaymentLinkModal] = useState(null); // { link, copied }

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const loadStripeStatus = useCallback(() => {
    if (!currentCompanyId) return;
    setStripeLoading(true);
    api.stripeConnect.status(currentCompanyId)
      .then(setStripeStatus)
      .catch(() => setStripeStatus({ connected: false }))
      .finally(() => setStripeLoading(false));
  }, [currentCompanyId]);

  useEffect(() => {
    loadStripeStatus();
    const params = new URLSearchParams(window.location.search);
    const result = params.get("stripe_connect");
    if (result === "success") {
      showToast("✅ Compte Stripe connecté avec succès");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (result === "error") {
      showToast("❌ Échec de connexion Stripe : " + (params.get("reason") || "erreur inconnue"));
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loadStripeStatus]);

  const handleConnectStripe = async () => {
    try {
      const { redirect_url } = await api.stripeConnect.initiate(currentCompanyId);
      window.location.href = redirect_url;
    } catch (err) {
      showToast("❌ Échec initialisation Stripe Connect : " + (err.message || "erreur inconnue"));
    }
  };

  const handleDisconnectStripe = async () => {
    if (!window.confirm("Déconnecter le compte Stripe de cette société ? Les liens de paiement existants cesseront de fonctionner.")) return;
    try {
      await api.stripeConnect.disconnect(currentCompanyId);
      loadStripeStatus();
      showToast("✅ Compte Stripe déconnecté");
    } catch (err) {
      showToast("❌ Échec déconnexion : " + (err.message || "erreur inconnue"));
    }
  };

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    Promise.all([
      api.abonnements.clientPlans(currentCompanyId),
      api.abonnements.clientSubs(currentCompanyId),
    ]).then(([p, s]) => { setPlans(p); setSubs(s); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  useEffect(() => { load(); }, [load]);

  const handleDeletePlan = async (id) => {
    if (!window.confirm("Supprimer ce plan ?")) return;
    await api.abonnements.deleteClientPlan(id).catch(console.error); load();
  };

  const handleSubStatus = async (id, status) => {
    const reason = status === "résilié" ? window.prompt("Motif de résiliation (optionnel) :") : null;
    await api.abonnements.clientSubStatus(id, status, reason).catch(console.error); load();
  };

  const handleDeleteSub = async (id, clientName) => {
    if (!window.confirm(`Supprimer définitivement l'abonnement de ${clientName} ? Cette action est irréversible.`)) return;
    try {
      await api.abonnements.deleteClientSub(id);
      load(); showToast("✅ Abonnement supprimé");
    } catch (err) {
      showToast("❌ Échec suppression : " + (err.message || "erreur inconnue"));
    }
  };

  const handleSendPaymentLink = async (id) => {
    try {
      const res = await api.abonnements.sendPaymentLink(id);
      if (res.email_sent) {
        showToast("✅ Lien de paiement envoyé par email");
      } else {
        // Brevo absent ou envoi échoué : le lien est quand même généré et
        // utilisable — on le propose en copier-coller plutôt que de laisser
        // l'admin sans solution (même principe que le lien d'invitation
        // expert-comptable dans ExpertComptable.jsx).
        setPaymentLinkModal({ link: res.payment_link, message: res.message, copied: false });
      }
    } catch (err) {
      showToast("❌ Échec envoi : " + (err.message || "erreur inconnue"));
    }
  };

  const copyPaymentLink = () => {
    if (!paymentLinkModal?.link) return;
    navigator.clipboard?.writeText(paymentLinkModal.link).then(() => {
      setPaymentLinkModal((m) => (m ? { ...m, copied: true } : m));
      setTimeout(() => setPaymentLinkModal((m) => (m ? { ...m, copied: false } : m)), 2500);
    }).catch(() => showToast("❌ Impossible de copier"));
  };

  const filteredSubs = subs.filter(s =>
    (!statusFilter || s.status === statusFilter) &&
    (!planFilter   || s.plan_id === parseInt(planFilter))
  );

  const INNER_TABS = [
    { id: "plans", label: `Plans (${plans.length})` },
    { id: "subs",  label: `Abonnés (${subs.length})` },
  ];

  return (
    <div>
      {toast && <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 2000, background: "#1a9e72", color: "#fff", padding: "12px 20px", borderRadius: 10, fontWeight: 600, fontSize: 14 }}>{toast}</div>}

      {/* Lien de paiement de secours — Brevo absent ou envoi échoué */}
      {paymentLinkModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setPaymentLinkModal(null)}>
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h3 className="modal-title">🔗 Lien de paiement à transmettre manuellement</h3>
              <button className="modal-close" onClick={() => setPaymentLinkModal(null)}>✕</button>
            </div>
            <div style={{ padding: "12px 0" }}>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>{paymentLinkModal.message}</div>
              <div style={{ background: "#f9fafb", border: "var(--border)", borderRadius: 8, padding: "12px 14px", fontFamily: "monospace", fontSize: 12, wordBreak: "break-all", color: "var(--text)", marginBottom: 14 }}>
                {paymentLinkModal.link}
              </div>
              <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#92400e", marginBottom: 16 }}>
                ℹ️ Une fois le moyen de paiement enregistré par le client via ce lien, le prélèvement automatique se fera normalement — peu importe comment le lien lui a été transmis.
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn" onClick={() => setPaymentLinkModal(null)}>Fermer</button>
                <button className="btn primary" onClick={copyPaymentLink}>
                  {paymentLinkModal.copied ? "✅ Copié !" : "📋 Copier le lien"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statut Stripe Connect */}
      {!stripeLoading && (
        <div className="card" style={{
          display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", marginBottom: 16,
          background: stripeStatus?.connected ? "#1a9e7210" : "#d9770610",
          border: `1px solid ${stripeStatus?.connected ? "#1a9e7230" : "#d9770630"}`,
        }}>
          <span style={{ fontSize: 20 }}>{stripeStatus?.connected ? "🟢" : "🟡"}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>
              {stripeStatus?.connected ? "Compte Stripe connecté" : "Aucun compte Stripe connecté"}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
              {stripeStatus?.connected
                ? "Les paiements de vos abonnés sont encaissés directement sur votre compte Stripe."
                : "Connectez votre compte Stripe pour pouvoir envoyer des liens de paiement à vos abonnés."}
            </div>
          </div>
          {stripeStatus?.connected
            ? <button className="btn" style={{ fontSize: 12, color: "#e24b4a" }} onClick={handleDisconnectStripe}>Déconnecter</button>
            : <button className="btn primary" style={{ fontSize: 12 }} onClick={handleConnectStripe}>Connecter Stripe</button>}
        </div>
      )}

      {/* Inner tabs */}
      <div style={{ display: "flex", gap: 2, borderBottom: "2px solid #e5e7eb", marginBottom: 20 }}>
        {INNER_TABS.map(t => (
          <button key={t.id} onClick={() => setInnerTab(t.id)} style={{ padding: "7px 16px", fontSize: 13, fontWeight: innerTab === t.id ? 700 : 500, cursor: "pointer", border: "none", background: "transparent", color: innerTab === t.id ? "var(--g)" : "var(--text-muted)", borderBottom: `2px solid ${innerTab === t.id ? "var(--g)" : "transparent"}`, marginBottom: -2 }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Plans ─────────────────────────────────────────────────── */}
      {innerTab === "plans" && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button className="btn primary" onClick={() => { setEditPlan(null); setShowPlanForm(true); }}>✨ Créer un plan</button>
          </div>
          {loading ? <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Chargement…</div> :
           plans.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: 56 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✨</div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Aucun plan tarifaire</div>
              <button className="btn primary" onClick={() => setShowPlanForm(true)}>Créer le premier plan</button>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
              {plans.map(p => <PlanCard key={p.id} plan={p} onEdit={pl => { setEditPlan(pl); setShowPlanForm(true); }} onDelete={handleDeletePlan} />)}
            </div>
          )}
        </div>
      )}

      {/* ── Abonnés ───────────────────────────────────────────────── */}
      {innerTab === "subs" && (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
            <button className="btn primary" onClick={() => { setEditSub(null); setShowSubForm(true); }}>+ Nouvel abonnement</button>
            <select className="input" style={{ width: "auto" }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">Tous statuts</option>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
            </select>
            <select className="input" style={{ width: "auto" }} value={planFilter} onChange={e => setPlanFilter(e.target.value)}>
              <option value="">Tous plans</option>
              {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-muted)" }}>{filteredSubs.length} abonnement(s)</div>
          </div>

          {loading ? <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Chargement…</div> :
           filteredSubs.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: 56 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Aucun abonnement client</div>
              <button className="btn primary" onClick={() => { setEditSub(null); setShowSubForm(true); }}>+ Créer le premier abonnement</button>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
                <thead>
                  <tr style={{ background: "#f8fafc", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
                    {["Client", "Plan", "Cycle", "Montant", "Statut", "Prochaine échéance", "Actions"].map(h => (
                      <th key={h} style={{ padding: "9px 14px", textAlign: h === "Montant" ? "right" : "left", fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredSubs.map(sub => {
                    const sc   = STATUS_CONFIG[sub.status] || STATUS_CONFIG.actif;
                    const dl   = daysLeft(sub.status === "trial" ? sub.trial_end_date : sub.next_billing_date);
                    return (
                      <tr key={sub.id} style={{ borderTop: "1px solid #f3f4f6", fontSize: 13 }}>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ fontWeight: 700 }}>{sub.client_name}</div>
                          {sub.client_email && <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{sub.client_email}</div>}
                        </td>
                        <td style={{ padding: "10px 14px", fontSize: 12 }}>{sub.plan_name || sub.plan_name_ref || "—"}</td>
                        <td style={{ padding: "10px 14px", fontSize: 12 }}>{sub.billing_cycle}</td>
                        <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700 }}>{fmt(sub.amount)} €</td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 10, fontWeight: 700, background: sc.bg, color: sc.color }}>
                            {sc.icon} {sc.label}
                            {sub.status === "trial" && dl !== null && <span> · J-{dl}</span>}
                          </span>
                        </td>
                        <td style={{ padding: "10px 14px", fontSize: 12, color: "var(--text-muted)" }}>
                          {fmtDate(sub.next_billing_date)}
                          {sub.status === "trial" && sub.trial_end_date && <div style={{ fontSize: 10 }}>Essai jusqu'au {fmtDate(sub.trial_end_date)}</div>}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            <button className="btn" style={{ fontSize: 10 }} onClick={() => { setEditSub(sub); setShowSubForm(true); }}>✏️ Modifier</button>
                            {sub.status === "actif"    && <button className="btn" style={{ fontSize: 10 }} onClick={() => handleSubStatus(sub.id, "suspendu")}>Suspendre</button>}
                            {sub.status === "suspendu" && <button className="btn" style={{ fontSize: 10, color: "#1a9e72" }} onClick={() => handleSubStatus(sub.id, "actif")}>Réactiver</button>}
                            {sub.status === "impayé"   && <button className="btn" style={{ fontSize: 10, color: "#1a9e72" }} onClick={() => handleSubStatus(sub.id, "actif")}>Marquer payé</button>}
                            {!["résilié"].includes(sub.status) && <button className="btn" style={{ fontSize: 10, color: "#e24b4a" }} onClick={() => handleSubStatus(sub.id, "résilié")}>Résilier</button>}
                            {["stripe","gocardless"].includes(sub.payment_method) && !sub.stripe_customer_id && !sub.gocardless_customer_id && sub.client_email &&
                              <button className="btn" style={{ fontSize: 10, color: "#185fa5" }} onClick={() => handleSendPaymentLink(sub.id)}>📧 Lien paiement</button>}
                            <button className="btn" style={{ fontSize: 10, color: "#e24b4a" }} onClick={() => handleDeleteSub(sub.id, sub.client_name)}>Supprimer</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showPlanForm && <PlanFormModal initial={editPlan} currentCompanyId={currentCompanyId} onClose={() => { setShowPlanForm(false); setEditPlan(null); }} onSaved={() => { load(); showToast(editPlan ? "✅ Plan modifié" : "✅ Plan créé"); }} />}
      {showSubForm  && <SubFormModal initial={editSub} plans={plans} currentCompanyId={currentCompanyId} onClose={() => { setShowSubForm(false); setEditSub(null); }} onSaved={() => { load(); showToast(editSub ? "✅ Abonnement modifié" : "✅ Abonnement créé"); }} />}
    </div>
  );
}
